import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useEditController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import type { SubmitHandler } from "react-hook-form";
import { SimpleForm } from "@/components/admin/simple-form";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import type { CrmDataProvider } from "../providers/types";
import type { BusinessArm, Sale, SalesFormData } from "../types";
import { getSalesErrorNotification } from "./salesErrorNotification";
import { ClassifyPermissionSwitch } from "./ClassifyPermissionSwitch";
import { ReassignPermissionSwitch } from "./ReassignPermissionSwitch";
import { SalesInputs } from "./SalesInputs";
import { isSwitchingToCorretajeWithProjects } from "./businessArms";
import {
  SalesProjectsInput,
  useActiveMembershipCount,
} from "./SalesProjectsInput";

/**
 * design-system.md section 8: a designed skeleton, never a spinner, while
 * the record loads.
 */
const SalesEditSkeleton = () => {
  const translate = useTranslate();
  return (
    <div
      aria-busy="true"
      role="status"
      className="max-w-lg w-full mx-auto mt-8 space-y-4"
    >
      <span className="sr-only">{translate("crm.common.loading")}</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} aria-hidden="true" className="h-9 w-full" />
      ))}
    </div>
  );
};

function EditToolbar() {
  return (
    <div className="flex justify-end gap-4">
      <CancelButton />
      <SaveButton />
    </div>
  );
}

export function SalesEdit() {
  const { record, isPending } = useEditController();

  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [pickedBusinessArm, setPickedBusinessArm] = useState<
    BusinessArm | null | undefined
  >(undefined);
  const activeMembershipCount = useActiveMembershipCount(record?.id);
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      if (!record) {
        throw new Error(
          translate("resources.sales.edit.record_not_found", {
            _: "Record not found",
          }),
        );
      }
      const updated = await dataProvider.salesUpdate(record.id, data);
      // Accumulated sales travel ONLY through the Direccion-only RPC, and
      // only when the number actually changed (the form resubmits it).
      const accumulated = data.accumulated_sales_at_onboarding;
      if (
        accumulated != null &&
        accumulated !== (record.accumulated_sales_at_onboarding ?? 0)
      ) {
        await dataProvider.setAccumulatedSalesAtOnboarding(
          record.id,
          accumulated,
        );
      }
      return updated;
    },
    onSuccess: () => {
      // The save bypasses react-admin's mutations: refresh cached rows.
      void queryClient.invalidateQueries({ queryKey: ["sales"] });
      void queryClient.invalidateQueries({ queryKey: ["org_chart_people"] });
      redirect("/sales");
      notify("resources.sales.edit.success", {
        messageArgs: {
          _: "User updated successfully",
        },
      });
    },
    onError: (error) => {
      const { message, args } = getSalesErrorNotification(
        error,
        "resources.sales.edit.error",
      );
      notify(message, {
        type: "error",
        messageArgs: { ...args, _: "An error occurred. Please try again." },
      });
    },
  });

  const onSubmit: SubmitHandler<SalesFormData> = async (data) => {
    // A Corretaje person keeps no projects: block the save with a calm
    // message until her active memberships are ended (the projects card
    // does that through its own mutations).
    if (
      isSwitchingToCorretajeWithProjects(
        record?.business_arm,
        data.business_arm,
        activeMembershipCount,
      )
    ) {
      notify("resources.sales.fields.corretaje_projects_blocked", {
        type: "warning",
      });
      return;
    }
    mutate(data);
  };

  if (isPending) {
    return <SalesEditSkeleton />;
  }

  return (
    // Record-level gate (Almikia Epic E3b, TASK-063): a lead_manager may
    // only reach this far for a user within her brazo scope, and never for
    // a peer lead_manager (ADR-ee2f59a9-TASK-060) -- a direct edit URL to
    // an out-of-scope user redirects back to the list instead of rendering
    // the form. Direccion's edit action was already granted regardless of
    // `record` by `useEditController`'s own recordless page-level check.
    <CanAccess
      resource="sales"
      action="edit"
      record={record}
      loading={<SalesEditSkeleton />}
      accessDenied={<Navigate to="/sales" replace />}
    >
      <div className="max-w-lg w-full mx-auto mt-8 space-y-4">
        <Card>
          <CardContent>
            <SimpleForm
              toolbar={<EditToolbar />}
              onSubmit={onSubmit as SubmitHandler<any>}
              record={record}
            >
              <SaleEditTitle />
              <SalesInputs onBusinessArmChange={setPickedBusinessArm} />
            </SimpleForm>
          </CardContent>
        </Card>
        {/* Almikia Epic E3b-2 (TASK-086): sales_projects is a separate
            resource from `sales`, so it lives outside the SimpleForm above
            and saves through its own mutations instead of the main "Save"
            button. */}
        {record ? (
          <SalesProjectsInput
            salesId={record.id}
            businessArm={pickedBusinessArm ?? record.business_arm}
            showCorretajeBlockedMessage={isSwitchingToCorretajeWithProjects(
              record.business_arm,
              pickedBusinessArm,
              activeMembershipCount,
            )}
          />
        ) : null}
        {/* Almikia Epic EL3 (TASK-085): Direccion-only permission switch, saved
            through its own RPC and hidden on the person's own profile. */}
        {record ? <ReassignPermissionSwitch sale={record} /> : null}
        {/* Almikia Epic EW3 (TASK-095): same pattern for the WhatsApp classify permission. */}
        {record ? <ClassifyPermissionSwitch sale={record} /> : null}
      </div>
    </CanAccess>
  );
}

const SaleEditTitle = () => {
  const record = useRecordContext<Sale>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <h2 className="text-lg font-semibold mb-4">
      {translate("resources.sales.edit.title", {
        name: `${record.first_name} ${record.last_name}`,
      })}
    </h2>
  );
};
